import { useRef } from 'react';
import { TextField } from '@components/base/inputs';
import { Button } from '@components/base/actions';
import { HStack, VStack } from '@components/base/layout';
import { Text, Icon } from '@components/base/display';
import { Plus, Trash2 } from 'lucide-react';

interface KeyValueEditorProps {
  readonly value: Array<{ key: string; value: string }>;
  readonly onChange: (value: Array<{ key: string; value: string }>) => void;
  readonly keyLabel?: string;
  readonly valueLabel?: string;
  readonly keyPlaceholder?: string;
  readonly valuePlaceholder?: string;
  readonly helperText?: string;
}

interface KeyValueRow {
  id: string;
  key: string;
  value: string;
}

let nextKvId = 0;
function generateKvId(): string {
  nextKvId += 1;
  return `kv-${nextKvId}-${Date.now()}`;
}

export function KeyValueEditor({
  value,
  onChange,
  keyLabel = 'Key',
  valueLabel = 'Value',
  keyPlaceholder = 'Enter key',
  valuePlaceholder = 'Enter value',
  helperText,
}: Readonly<KeyValueEditorProps>) {
  const rowsRef = useRef<KeyValueRow[]>([]);

  // Reconcile external value with internal rows that carry stable IDs
  const externalRows = value.length > 0 ? value : [{ key: '', value: '' }];
  rowsRef.current = externalRows.map((r, i) => ({
    id: rowsRef.current[i]?.id ?? generateKvId(),
    key: r.key,
    value: r.value,
  }));
  const rows = rowsRef.current;

  function updateRow(index: number, field: 'key' | 'value', newValue: string) {
    const updated = rows.map((row, i) =>
      i === index ? { ...row, [field]: newValue } : row,
    );
    rowsRef.current = updated;
    onChange(updated.map(({ key, value: v }) => ({ key, value: v })));
  }

  function addRow() {
    const newRows = [...rows, { id: generateKvId(), key: '', value: '' }];
    rowsRef.current = newRows;
    onChange(newRows.map(({ key, value: v }) => ({ key, value: v })));
  }

  function removeRow(index: number) {
    if (rows.length <= 1) {
      const reset = [{ id: generateKvId(), key: '', value: '' }];
      rowsRef.current = reset;
      onChange([{ key: '', value: '' }]);
      return;
    }
    const filtered = rows.filter((_, i) => i !== index);
    rowsRef.current = filtered;
    onChange(filtered.map(({ key, value: v }) => ({ key, value: v })));
  }

  return (
    <VStack gap={3}>
      <HStack gap={3} align="end">
        <Text size="xs" weight="medium" style={{ flex: 1 }}>
          {keyLabel}
        </Text>
        <Text size="xs" weight="medium" style={{ flex: 1 }}>
          {valueLabel}
        </Text>
        <div style={{ width: 36 }} />
      </HStack>

      {rows.map((row, index) => (
        <HStack key={row.id} gap={3} align="start">
          <div style={{ flex: 1 }}>
            <TextField
              size="sm"
              placeholder={keyPlaceholder}
              value={row.key}
              onChange={(e) => updateRow(index, 'key', e.target.value)}
            />
          </div>
          <div style={{ flex: 1 }}>
            <textarea
              rows={1}
              placeholder={valuePlaceholder}
              value={row.value}
              onChange={(e) => updateRow(index, 'value', e.target.value)}
              className="w-full resize-y rounded-md border border-secondary-300 px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-500"
            />
          </div>
          <Button
            variant="ghost"
            size="sm"
            type="button"
            onClick={() => removeRow(index)}
            aria-label="Remove row"
          >
            <Icon icon={Trash2} size="sm" color="error" />
          </Button>
        </HStack>
      ))}

      <div>
        <Button
          variant="ghost"
          size="sm"
          type="button"
          leftIcon={<Icon icon={Plus} size="sm" />}
          onClick={addRow}
        >
          Add
        </Button>
      </div>

      {helperText && (
        <Text size="xs" color="muted">
          {helperText}
        </Text>
      )}
    </VStack>
  );
}
